<template>
  <section
    class="padding-top-4xl size-md tablet:size-sm"
    style="--sale-theme-color: var(--fa-teal); --sale-theme-color-dk: var(--fa-dk-teal); --sale-link-color: var(--fa-navy)"
  >
    <div class="container">
      <div class="row roomy margin-bottom-xl">
        <div class="column-12">
          <div
            :class="[
              'display-flex flex-column tablet:flex-row message size-reset-md margin-bottom-0',
              isProBacker || isBackerWithoutSubscription ? 'align-stretch tablet:align-top' : 'align-middle'
            ]"
            style="--message-background: var(--sale-theme-color); --message-border-color: var(--sale-theme-color)"
          >
            <button
              v-if="hasPromoCode"
              class="card flat padding-x-xl padding-y-md tablet:margin-bottom-0 tablet:margin-right-2xl"
              style="background-color: var(--sale-theme-color); border: var(--border-width-sm) dashed var(--fa-navy)"
              data-balloon-pos="up"
              :aria-label="justCopied === 'copyPromoCode' ? 'Copied!' : 'Copy Promo Code'"
              @click="copyPromoCode"
            >
              <h3
                class="h4 text-center text-uppercase"
                style="letter-spacing: var(--letter-spacing-2xl); font-weight: var(--font-weight-black)"
              >
                <span class="sr-only">Copy</span>
                <span
                  class="display-block margin-bottom-4xs"
                  style="color: var(--fa-navy)"
                  >{{ promoCode }}</span
                >
                <span
                  class="display-block size-xs text-nowrap"
                  style="color: var(--sale-theme-color-dk)"
                  >promo code</span
                >
              </h3>
            </button>
            <div
              v-else
              class="card padding-x-xl padding-y-md tablet:margin-bottom-0 tablet:margin-right-2xl"
              style="background-color: var(--fa-navy)"
            >
              <h3
                class="h4 text-center text-uppercase"
                style="letter-spacing: var(--letter-spacing-2xl); font-weight: var(--font-weight-black)"
              >
                <span
                  class="display-block margin-bottom-4xs"
                  style="color: var(--white)"
                  >{{ discount }}% off</span
                >
                <span
                  class="display-block size-xs text-nowrap"
                  style="color: var(--sale-theme-color)"
                  >ends in {{ daysLeft }} {{ formatPlural(daysLeft, 'day') }}</span
                >
              </h3>
            </div>
            <div v-if="isProBacker || isBackerWithoutSubscription">
              <p style="--link-color: var(--fa-navy); --link-hover-color: var(--fa-dk-blue)">
                Nearly ten years ago, you helped us make history as the most successful software Kickstarter ever. As a thank you, pre-order Font Awesome 7 with
                a <span v-if="isBackerWithoutSubscription">Pro or</span> Pro+ plan by <strong>{{ saleEndDateFormatted }}</strong> and lock in a
                <strong>{{ discount }}% annual discount</strong> for life!
                <template v-if="hasPromoCode"
                  >Use code <strong>{{ promoCode }}</strong> when updating your subscription.
                  <site-link :to="{ name: 'support', hash: '#promo-codes' }">
                    <font-awesome-icon
                      :icon="$v6.solid.faInfoCircle"
                      fixed-width
                    />
                    <span class="sr-only">Learn more</span>
                  </site-link>
                </template>
                <template v-else> Plus, get instant access to Font Awesome 6.</template>
              </p>
              <div class="row roomy margin-top-xl align-stretch">
                <div class="column-6 display-flex flex-column align-end">
                  <div>
                    <img
                      src="https://img.fortawesome.com/1ce05b4b/dave-signature.svg"
                      alt="Signed Dave Gandy"
                      style="max-height: 40px; margin-right: var(--spacing-md)"
                    />
                  </div>
                  <div>
                    <p class="strong">
                      Dave Gandy
                      <span
                        class="display-block size-sm"
                        style="color: var(--sale-theme-color-dk)"
                        >Founder + CEO</span
                      >
                    </p>
                  </div>
                </div>
                <div class="column-6 display-flex flex-column align-end">
                  <div>
                    <img
                      src="https://img.fortawesome.com/1ce05b4b/travis-signature.svg"
                      alt="Signed Travis Chase"
                      style="max-height: 40px; margin-right: var(--spacing-md)"
                    />
                  </div>
                  <div>
                    <p class="strong">
                      Travis Chase
                      <span
                        class="display-block size-sm"
                        style="color: var(--sale-theme-color-dk)"
                        >Founder + COO</span
                      >
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <p style="--link-color: var(--fa-navy); --link-hover-color: var(--fa-dk-blue)">
                <span v-if="hasPromoCode">Upgrade to a </span><span v-else>Pre-order Font Awesome 7 with a Pro or</span> Pro+ plan by
                <strong>{{ saleEndDateFormatted }}</strong> and lock-in a {{ discount }}% annual discount!
                <template v-if="hasPromoCode"
                  >Use code <strong>{{ promoCode }}</strong> when updating your subscription.
                  <site-link :to="{ name: 'support', hash: '#promo-codes' }">
                    <font-awesome-icon
                      :icon="$v6.solid.faInfoCircle"
                      fixed-width
                    />
                    <span class="sr-only">Learn more</span>
                  </site-link>
                </template>
                <template v-else> Plus, get instant access to Font Awesome 6.</template>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import pluralize from 'pluralize'
  import { mapGetters } from 'vuex'
  import { preSaleEnd, timeToPreSaleEnd } from 'app/views/timerUtils'

  export default {
    name: 'PromoAlert',

    props: {
      promoCode: {
        type: String
      },
      discount: {
        type: String,
        default: '20'
      }
    },

    data() {
      return {
        justCopied: ''
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),
      ...mapGetters('subscriptions', ['currentSubscription']),

      hasPromoCode() {
        return this.promoCode
      },

      isProBacker() {
        if (!this.currentAccount) {
          return false
        }

        return this.currentAccount.isBacker && this.currentSubscription
      },

      isBackerWithoutSubscription() {
        if (!this.currentAccount) {
          return false
        }

        return this.currentAccount.isBacker && !this.currentSubscription
      },

      saleEndDateFormatted() {
        return preSaleEnd.toFormat('MMMM d, yyyy')
      },

      daysLeft() {
        return timeToPreSaleEnd().days
      }
    },

    methods: {
      markCopied(what) {
        this.justCopied = what
        setTimeout(() => {
          this.justCopied = null
        }, 2000)
      },

      copyPromoCode() {
        this.$clipboard(`${this.promoCode}`)
        this.markCopied('copyPromoCode')
        this.$posthog.capture('promo_code_copy_click', { context: 'plans_view' })
      },

      formatPlural(number, word) {
        return pluralize(word, number)
      }
    }
  }
</script>
